import { useState, useEffect, useRef } from 'react';
import { useFetcher } from '@remix-run/react';

interface EditableImageData {
    ID: number;
    __typename: string;
}

interface UseAIRevertApiOptions {
    onCompleted?: (data: EditableImageData) => void;
    onError?: (error: string) => void;
}

interface RevertApiResponse {
    success: boolean;
    data?: {
        imageRevert?: EditableImageData;
    };
    error?: string;
}

/**
 * Custom hook for AI image revert using Remix API route
 */
export const useAIRevertApi = (
    libraryImageID: number,
    originalImageUrl: string
) => {
    const fetcher = useFetcher<RevertApiResponse>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const optionsRef = useRef<UseAIRevertApiOptions>();

    useEffect(() => {
        if (fetcher.state === 'idle' && fetcher.data) {
            if (fetcher.data.success) {
                if (fetcher.data.data?.imageRevert) {
                    optionsRef.current?.onCompleted?.(
                        fetcher.data.data.imageRevert
                    );
                }
            } else {
                const errorMessage =
                    fetcher.data.error || 'Unknown error occurred';
                setError(errorMessage);
                optionsRef.current?.onError?.(errorMessage);
            }
            setIsLoading(false);
        } else if (fetcher.state === 'idle' && fetcher.data === undefined) {
            setIsLoading(false);
            setError(null);
        }
    }, [fetcher.state, fetcher.data]);

    const triggerRevert = async (options?: UseAIRevertApiOptions) => {
        setIsLoading(true);
        setError(null);
        optionsRef.current = options;

        const formData = new FormData();
        formData.append('libraryImageID', libraryImageID.toString());
        formData.append('originalImageUrl', originalImageUrl);

        fetcher.submit(formData, {
            method: 'POST',
            action: '/remix-api/admin/revert-ai-edit',
        });
    };

    return [
        triggerRevert,
        {
            loading: isLoading || fetcher.state === 'submitting',
            error:
                error ||
                (fetcher.data?.success === false ? fetcher.data.error : null),
            data: fetcher.data?.success ? fetcher.data.data?.imageRevert : null,
        },
    ] as const;
};
